import { useEffect, useId, useRef, useState } from 'react'
import type { JSX } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import {
  BookOpenTextIcon,
  BotIcon,
  Button,
  CheckIcon,
  ChevronLeftIcon,
  XIcon,
  CodeXmlIcon,
  CopyIcon,
  Flex,
  IconButton,
  LightbulbIcon,
  PlayIcon,
  TerminalIcon,
  Text,
} from '@buffer-mono/popcorn'

import { copyToClipboard, copyTokenToClipboard } from '../../helpers/copyToken'

import styles from './OnboardingCallout.module.css'

const GRAPHQL_EXPLORER_BASE_URL = 'https://developers.buffer.com/explorer.html'
const CLI_DOCS_URL = 'https://developers.buffer.com/guides/cli.html'
const INTEGRATIONS_URL = '/settings/integrations'

const AGENT_PROMPT =
  "Create a new idea in Buffer about creating ideas from Buffer's API"

const CLI_INSTRUCTIONS = `npm install -g @bufferapp/cli
buffer init
buffer ideas create --text "My first idea from the Buffer CLI"`

const createExampleMutation = (
  organizationId: string,
): string => `mutation CreateIdea {
  createIdea(input: {
    organizationId: "${organizationId}",
    content: {
      title: "New Idea from GraphQL API"
      text: "This is the text of the new idea created via the GraphQL API."
    }
  }) {
    ... on Idea {
      id
      content {
        title
        text
      }
    }
  }
}`

type ConnectionMethod = 'graphql' | 'agent' | 'cli'

const CONNECTION_METHOD_COPY: Record<
  ConnectionMethod,
  { name: string; instruction: string }
> = {
  graphql: {
    name: 'GraphQL',
    instruction: 'Use this query to make your first API call:',
  },
  agent: {
    name: 'AI Agent',
    instruction: 'Send this prompt to your agent to make your first API call',
  },
  cli: {
    name: 'Terminal',
    instruction: 'Paste these instructions into your terminal',
  },
}

const highlightGraphQL = (code: string): JSX.Element[] => {
  const tokens: JSX.Element[] = []
  let key = 0
  const regex = /(mutation|on)\b|([a-zA-Z]\w*)|("[^"]*")|(\.\.\.\s*)|([\s\S])/g

  let match

  while ((match = regex.exec(code)) !== null) {
    const [fullMatch, keyword, fieldName, stringValue, spread] = match

    if (keyword) {
      tokens.push(
        <span key={key++} className={styles.keyword}>
          {keyword}
        </span>,
      )
    } else if (fieldName) {
      tokens.push(
        <span key={key++} className={styles.fieldName}>
          {fieldName}
        </span>,
      )
    } else if (stringValue) {
      tokens.push(
        <span key={key++} className={styles.stringValue}>
          {stringValue}
        </span>,
      )
    } else if (spread) {
      tokens.push(
        <span key={key++} className={styles.keyword}>
          {spread}
        </span>,
      )
    } else {
      tokens.push(<span key={key++}>{fullMatch}</span>)
    }
  }

  return tokens
}

const CodeSnippet = ({
  code,
  highlight = false,
}: {
  code: string
  highlight?: boolean
}): JSX.Element => {
  return (
    <div className={styles.codeSnippet}>
      <pre className={styles.code}>
        {highlight ? highlightGraphQL(code) : code}
      </pre>
      <IconButton
        label="Copy to clipboard"
        variant="tertiary"
        size="small"
        className={styles.copySnippetButton}
        onClick={(): void => {
          void copyToClipboard(code)
        }}
      >
        <CopyIcon />
      </IconButton>
    </div>
  )
}

interface OnboardingCalloutProps {
  token: string
  organizationId: string | null
  onDismiss: () => void
}

export const OnboardingCallout = ({
  token,
  organizationId,
  onDismiss,
}: OnboardingCalloutProps): JSX.Element => {
  const [method, setMethod] = useState<ConnectionMethod | null>(null)
  const [hasTriedExplorer, setHasTriedExplorer] = useState(false)
  const navigate = useNavigate()
  const stepTitleId = useId()
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const methodButtonRefs = useRef(
    new Map<ConnectionMethod, HTMLButtonElement | null>(),
  )
  const previousMethodRef = useRef<ConnectionMethod | null>(null)

  // Each step replaces the element that was clicked, so focus would fall back
  // to the body without moving it onto the newly rendered step.
  useEffect(() => {
    if (method !== null) {
      backButtonRef.current?.focus()
    } else if (previousMethodRef.current !== null) {
      methodButtonRefs.current.get(previousMethodRef.current)?.focus()
    }

    previousMethodRef.current = method
  }, [method])

  const exampleMutation = createExampleMutation(
    organizationId ?? 'YOUR_ORGANIZATION_ID',
  )

  const explorerUrl = `${GRAPHQL_EXPLORER_BASE_URL}?query=${encodeURIComponent(
    exampleMutation,
  )}`

  const handleOpenExplorer = (): void => {
    void copyTokenToClipboard(token)
    window.open(explorerUrl, '_blank', 'noopener,noreferrer')
    setHasTriedExplorer(true)
  }

  const handleReopenExplorer = (): void => {
    window.open(explorerUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCheckInIdeas = (): void => {
    onDismiss()
    navigate('/create/ideas')
  }

  const dismissButton = (
    <IconButton
      label="Dismiss"
      variant="tertiary"
      size="small"
      onClick={onDismiss}
    >
      <XIcon />
    </IconButton>
  )

  if (method === null) {
    return (
      <div className={styles.container}>
        <Flex justify="between" align="start" gap="md">
          <Text weight="medium" size="lg">
            Nice! 🎉 Now let’s try your first API call and create an Idea.
          </Text>
          {dismissButton}
        </Flex>

        <Text weight="medium" color="subtle">
          How would you like to connect to the API?
        </Text>

        <Flex gap="md" wrap="wrap">
          <Button
            variant="secondary"
            ref={(node: HTMLButtonElement | null): void => {
              methodButtonRefs.current.set('graphql', node)
            }}
            onClick={(): void => setMethod('graphql')}
          >
            <CodeXmlIcon />
            GraphQL Query
          </Button>
          <Button
            variant="secondary"
            ref={(node: HTMLButtonElement | null): void => {
              methodButtonRefs.current.set('agent', node)
            }}
            onClick={(): void => setMethod('agent')}
          >
            <BotIcon />
            AI Agent
          </Button>
          <Button
            variant="secondary"
            ref={(node: HTMLButtonElement | null): void => {
              methodButtonRefs.current.set('cli', node)
            }}
            onClick={(): void => setMethod('cli')}
          >
            <TerminalIcon />
            Terminal (CLI)
          </Button>
        </Flex>
      </div>
    )
  }

  const instruction =
    method === 'graphql' && hasTriedExplorer
      ? "Great! Once you're done check it out in Ideas:"
      : CONNECTION_METHOD_COPY[method].instruction

  return (
    <section className={styles.container} aria-labelledby={stepTitleId}>
      <Flex justify="between" align="start" gap="md">
        <Flex align="center" gap="xs">
          <IconButton
            label="Back"
            variant="tertiary"
            size="small"
            ref={backButtonRef}
            onClick={(): void => setMethod(null)}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Text weight="medium" id={stepTitleId}>
            {CONNECTION_METHOD_COPY[method].name} -{' '}
            <Text as="span">{instruction}</Text>
          </Text>
        </Flex>
        {dismissButton}
      </Flex>

      {method === 'graphql' && (
        <>
          <CodeSnippet code={exampleMutation} highlight />
          {hasTriedExplorer ? (
            <Flex gap="sm">
              <Button variant="secondary" onClick={handleReopenExplorer}>
                <CheckIcon />
                Opened in Explorer
              </Button>
              <Button variant="primary" onClick={handleCheckInIdeas}>
                <LightbulbIcon />
                Check It in Ideas
              </Button>
            </Flex>
          ) : (
            <div>
              <Button variant="primary" onClick={handleOpenExplorer}>
                <PlayIcon />
                Copy Key & Try It in API Explorer
              </Button>
            </div>
          )}
        </>
      )}

      {method === 'agent' && (
        <>
          <CodeSnippet code={AGENT_PROMPT} />
          <div>
            <Button
              as={NavLink}
              to={INTEGRATIONS_URL}
              variant="primary"
              className={styles.ctaLink}
            >
              <BotIcon />
              Connect an Agent
            </Button>
          </div>
        </>
      )}

      {method === 'cli' && (
        <>
          <CodeSnippet code={CLI_INSTRUCTIONS} />
          <div>
            <Button
              as="a"
              href={CLI_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              className={styles.ctaLink}
            >
              <BookOpenTextIcon />
              Full CLI Instructions
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
